import { History } from 'history'
import { action, makeObservable } from 'mobx'

import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import commonRoutes, { getTransitionPath } from '../../constants/commonRoutes'
import { redirectToLogin } from '../../utils/loginPortal'
import { NOOP } from '../../utils/noop'
import InitialState from '../InitialState'
import RootStore from '../Root.store'

export interface IDisplayViewParams {
  state?: any
  viewMode?: string
  action?: string
  id?: string
  form?: string
}

export interface IDisplayLoginViewOptions {
  /**
   * Carry the current `return_to` forward onto `/login` (STX1-540).
   * Default is a clean, context-free login: navigating to `/login` is
   * intrinsically fresh (the URL is rebuilt from the route, not inherited),
   * so logout and every other fresh-login bounce drop all carried context
   * automatically — no param enumeration. Only the unauth bootstrap, which
   * must keep the deep link alive for the post-login fly-over, opts in.
   */
  preserveReturnTo?: boolean
}

// TODO: REVISIT IT

// This sometimes doubles as both a UI store and a dump for unsorted actions
export default abstract class CommonStore {
  public navigationListeners = []
  public history: History = {
    location: { pathname: '', search: '', state: {}, hash: '', key: undefined },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    push: (route: string, state?: any) => null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    replace: (route: string) => null,
    goBack: NOOP,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    listen: callback => {
      throw new Error('History not yet initialized.')
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    go: (n: number) => null,
  }

  // This will feel better as a function
  public awaitTransition = Promise.resolve()

  public constructor(
    protected readonly state: InitialState,
    protected readonly root: RootStore,
  ) {
    makeObservable(this)
  }

  public initHistory(history: History) {
    this.history = history
    if (this.state.initialRoute) {
      this.history.push(this.state.initialRoute)
    }
  }

  public hideNavBar() {
    return
  }

  public addNavigationListener(callback) {
    this.navigationListeners.push(callback)
  }
  public removeNavigationListener(callback) {
    this.navigationListeners = this.navigationListeners.filter(
      listener => listener !== callback,
    )
  }

  public _displayView(route: string, params: IDisplayViewParams = {}) {
    this.root.eventsStore.dispatch(e.COLLECT_TELEMETRY, {
      event: {
        eventCategory: 'PageView',
        eventAction: 'Change',
        eventLabel: window.location.pathname.split('/')[2],
        eventValue: 1,
      },
    })
    if (!this.history) {
      return
    }

    const transitionPath = getTransitionPath(
      route,
      this.state.activeOrInitProjectCode,
    )

    const { state, viewMode, ...queryParams } = params
    const queryString = Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join('&')
    const transitionRoute = `${transitionPath}${
      queryString ? '?' + queryString : ''
    }${viewMode ? '#' + viewMode : ''}`

    const { pathname, search, hash } = this.history.location
    const currentRoute = pathname + search + hash

    if (currentRoute !== transitionRoute) {
      // Force a URL change to trigger a re-render. Must be before the push.
      if (viewMode && `#${viewMode}` !== hash) {
        window.location.hash = viewMode
      }

      this.history.push(transitionRoute, state)

      // Tells listeners that the current view is changing
      this.navigationListeners.forEach(callback => {
        callback(transitionRoute)
      })
    }
  }

  public displayDefaultView = () => {
    this._displayView('/')
  }

  public displayLoginView = (
    options: IDisplayLoginViewOptions = {},
  ): boolean => {
    return redirectToLogin(
      { pathname: commonRoutes.LOGIN },
      { omitReturnTo: !options.preserveReturnTo },
    )
  }
  public displayInfoPage = (errorCode, payload) => {
    this._displayView(
      commonRoutes.INFO.replace(':code?', errorCode).replace(
        ':payload?',
        payload,
      ),
    )
  }

  public displayResetPasswordView(resetCode = '', state?: any) {
    void state
    const resetPasswordPath = commonRoutes.RESET_PASSWORD.replace(
      ':resetCode',
      resetCode,
    )
    redirectToLogin({ pathname: resetPasswordPath })
  }

  public displayHomeView = (params?: IDisplayViewParams) => {
    this._displayView(commonRoutes.HOME, params)
  }

  public displayFormsView = (params?: IDisplayViewParams) => {
    this._displayView(commonRoutes.FORMS, params)
  }

  public displayFormTypesView = () => {
    this._displayView(commonRoutes.FORM_TYPES)
  }

  public abstract displayDeliveriesView(viewMode?: string): void

  public abstract displayDeliveryDetailsView(id?: string): void

  public abstract displaySharedDeliveryView(id?: string): void

  public abstract openDeliveryView(): void

  public abstract displayPhotoView(photo): void

  public abstract displayNotifications(): void

  public abstract displayAuthSuccessRoute(): void

  public abstract openChat(): void

  @action.bound
  public handleOpeningChatWithUser(userId: string) {
    this.state.resetIdOfUserDisplayedOnGlobalCard()
    this.state.setRecipientIdForChatInit(userId)
    this.openChat()
  }

  public abstract get defaultAvailablePage(): string | undefined
}
